import React from "react";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

/**
 * Placeholder matching the ScenarioCard footprint so the gallery keeps its
 * layout while live data loads. Demo mode is synchronous and never uses these.
 */
export const ScenarioCardSkeleton: React.FC = () => (
  <Card
    className="relative overflow-hidden"
    style={{ borderLeft: "3px solid var(--mc-info-border)" }}
  >
    <CardContent className="relative p-6">
      {/* Header */}
      <div className="mb-4">
        <Skeleton className="h-5 w-24 mb-2 rounded-full" />
        <Skeleton className="h-6 w-3/4 mb-2" />
        <Skeleton className="h-4 w-full mb-1" />
        <Skeleton className="h-4 w-2/3" />
      </div>

      {/* Net Cash Impact */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-5 w-24" />
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <Skeleton className="h-3 w-16 mb-2" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    </CardContent>

    <div className="relative bg-white px-6 pb-6 pt-3 border-t border-gray-100">
      <Skeleton className="h-8 w-full" />
    </div>
  </Card>
);

export const ScenarioCardGridSkeleton: React.FC<{ count?: number }> = ({
  count = 6,
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
    {Array.from({ length: count }, (_, i) => (
      <ScenarioCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Placeholder for the four scenario detail metric cards. Rendered until every
 * numeric input has settled so figures never appear part-computed.
 */
export const ScenarioMetricsSkeleton: React.FC<{ labels: string[] }> = ({
  labels,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    {labels.map((label) => (
      <Card
        key={label}
        style={{ borderLeft: "3px solid var(--mc-info-border)" }}
      >
        <CardContent className="p-4 flex flex-col justify-between h-full gap-3">
          <div className="flex items-start justify-between">
            <span className="text-base font-medium text-neutral-800">
              {label}
            </span>
            <Skeleton className="h-9 w-9 rounded-lg" />
          </div>
          <div>
            <Skeleton className="h-7 w-28 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="pt-2 border-t">
            <Skeleton className="h-4 w-32" />
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Full-page placeholder for the scenario detail view while the scenario itself
 * is still propagating into context.
 */
export const ScenarioDetailSkeleton: React.FC<{ metricLabels: string[] }> = ({
  metricLabels,
}) => (
  <div className="space-y-6">
    <div className="flex items-start gap-4">
      <Skeleton className="h-9 w-20" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-7 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <div className="flex items-center gap-2 pt-1">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-24 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
    </div>
    <ScenarioMetricsSkeleton labels={metricLabels} />
  </div>
);
